import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  addDays,
  addYears,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  max as maxDate,
  min as minDate,
  parseISO,
  startOfDay,
  subDays,
  subYears,
} from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import * as LucideIcons from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import { acwrToHeatmapColor } from '../../utils/acwrHeatmapColor';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Switch } from '../ui/switch';
import TrainingLogDayModal from './TrainingLogDayModal';
import TrainingPlannerModal from './TrainingPlannerModal';
import { Badge } from '../ui/badge';
import SessionPickerModal from '../training/SessionPickerModal';

const { BookMarked, Calendar, Dumbbell, Loader2, Plus, Search, Settings } = LucideIcons;

function itemMatchesSearchLower(item, qLower) {
  if (!qLower) return false;
  const title = (item.title || '').toLowerCase();
  const summary = (item.summary || '').toLowerCase();
  return title.includes(qLower) || summary.includes(qLower);
}

/**
 * Draft stays local so the timeline does not re-render while typing.
 * Applies search (and scroll-to-hit in parent) only when Enter is pressed.
 */
const TrainingLogOpenSearchInput = memo(function TrainingLogOpenSearchInput({
  committedQuery,
  onCommittedChange,
  placeholder,
  ariaLabel,
  busy,
  busyLabel,
}) {
  const [draft, setDraft] = useState(committedQuery);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onCommittedChangeRef = useRef(onCommittedChange);
  onCommittedChangeRef.current = onCommittedChange;

  return (
    <div className="relative w-full min-w-0">
      <Search
        className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      {busy && (
        <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />
          <span className="sr-only">{busyLabel}</span>
        </span>
      )}
      <Input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          onCommittedChangeRef.current(draftRef.current);
        }}
        className={cn(
          'h-8 border-border/80 bg-background/95 pl-8 text-xs shadow-sm',
          busy && 'pr-9'
        )}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-busy={busy}
      />
    </div>
  );
});

function clampIntervalToPolicy(start, end, policyMin, policyMax) {
  let s = start < policyMin ? policyMin : start;
  let e = end > policyMax ? policyMax : end;
  if (s > e) return { start: policyMin, end: policyMax };
  return { start: s, end: e };
}

export const TRAINING_LOG_INSET_PEEK = 72;
const PEEK_W = TRAINING_LOG_INSET_PEEK;
/** Wide panel width (60% of former 420px default). */
const OPEN_W = Math.round(420 * 0.6);
/** App header height (`h-16`). */
export const TRAINING_LOG_BELOW_HEADER_TOP_PX = 64;
/** Matches dashboard `px-6` / `pb-6`: dock `right`/`bottom` inset from viewport; also added to `marginRight` so grid gap stays 16px with `pr-4`. */
export const TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX = 24;
/** Must stay in sync with `GridDashboard` `margin={[16, 16]}` and dashboard `pr-4` when the dock is shown. */
const GRID_WIDGET_MARGIN_PX = 16;
/** Floating diary control (`Button` `size="icon"` → `h-9 w-9`). */
const TRAINING_LOG_LAUNCHER_BTN_PX = 36;
/** Dashboard main column `pr-4` when training log is visible (matches `GRID_WIDGET_MARGIN_PX`). */
const DASHBOARD_MAIN_PR_WHEN_DOCK_PX = 16;
/**
 * When hidden: `marginRight = inset + VIEWPORT_GUTTER`, launcher `right = VIEWPORT_GUTTER`, column `pr-4`.
 * Gap (widgets → icon) = `GRID_WIDGET_MARGIN_PX`.
 */
export const TRAINING_LOG_DOCK_HIDDEN_RESERVE_PX =
  GRID_WIDGET_MARGIN_PX + TRAINING_LOG_LAUNCHER_BTN_PX - DASHBOARD_MAIN_PR_WHEN_DOCK_PX;
/** Dashboard main column `pt-4` (must match `Dashboard.jsx`). */
const DASHBOARD_MAIN_TOP_PAD_PX = 16;
/** `GridDashboard` `margin[1]` — first row sits this many px below the padded column start. */
const GRID_LAYOUT_MARGIN_Y_PX = 16;
/** Top edge lines up with the first grid row (header + column padding + RGL top margin). */
export const TRAINING_LOG_DOCK_TOP_PX =
  TRAINING_LOG_BELOW_HEADER_TOP_PX + DASHBOARD_MAIN_TOP_PAD_PX + GRID_LAYOUT_MARGIN_Y_PX;
/** Main training-log toggle (book) — distinct from outline “tool” controls (date jump, settings). */
const TRAINING_LOG_HEADER_ICON_BTN_CLASS =
  'h-8 w-8 shrink-0 rounded-full border border-border/80 bg-background/95 text-muted-foreground shadow-md backdrop-blur-sm hover:bg-accent hover:text-accent-foreground';
/** Settings / jump-date tool buttons — opaque `bg-card` so timeline and scroll layers never read through. */
const TRAINING_LOG_DATE_TOOL_BTN_CLASS =
  'h-9 w-9 shrink-0 rounded-full border border-border/80 bg-card text-card-foreground shadow-md hover:bg-accent hover:text-accent-foreground';
const TRAINING_LOG_MAIN_DIARY_BTN_CLASS = cn(TRAINING_LOG_HEADER_ICON_BTN_CLASS, 'h-9 w-9');
/** Default past share when only past or only future days exist (balanced calendar uses P/(P+F)). */
const PAST_FRAC = 0.2;
/** Minimum px between consecutive calendar rows (cluster h-11 ≈ 44px). */
const MIN_DAY_GAP = 48;
/** Inner margin inside usable U for past/future day stacks. */
const U_STACK_MARGIN = 8;
/** Matches timeline track column top inset (line vs. dot Y must use same coordinate system). */
const TIMELINE_PAD_TOP = 8;
/** After jump / “today”, align target day’s dot this many px below the top of the timeline scroll area (first day on the rail). */
const DAY_SCROLL_TOP_INSET = TIMELINE_PAD_TOP;
/** Open-mode search auto-scroll: extra offset so entry titles clear the title + search header. */
const OPEN_SEARCH_HIT_SCROLL_EXTRA_TOP_PX = 20;
/**
 * ~2 months each side of the anchor (visible focus): initial load, each edge extend, and jump-date window.
 * Scrolling near the top/bottom of the loaded range loads another chunk of the same size (past vs future).
 */
const TIMELINE_LOAD_RADIUS_DAYS = 62;
const EXTEND_CHUNK_DAYS = TIMELINE_LOAD_RADIUS_DAYS;
/** Look-ahead: at least this many px from the viewport edge before prefetching the next chunk. */
const SCROLL_EDGE_PX_MIN = 220;
/** Fraction of timeline viewport height — larger = earlier fetch while still scrolling toward the bound. */
const SCROLL_EDGE_VIEWPORT_FRAC = 0.55;

/**
 * Keep top/bottom prefetch bands disjoint: need edgePx <= maxScroll/2 so some scroll positions
 * trigger neither extend. Cap below 0.5*maxScroll; use ~0.48 so we can still prefetch earlier than 0.4.
 */
function computeScrollEdgePx(clientHeight, maxScroll) {
  const raw = Math.max(SCROLL_EDGE_PX_MIN, Math.round(clientHeight * SCROLL_EDGE_VIEWPORT_FRAC));
  if (maxScroll <= 0) return raw;
  const cap = Math.max(SCROLL_EDGE_PX_MIN, Math.floor(maxScroll * 0.48));
  return Math.min(raw, cap);
}

/** Prefetch when this much empty space appears between viewport edge and nearest cluster (scrollTop alone misses this after balance scroll). */
function clusterGapLeadPx(clientHeight) {
  return Math.max(140, Math.round(clientHeight * 0.3));
}
/** Jump / scroll fetch: default window ±2 years from today; widened to include earliest/latest log/activity when beyond that. */
const JUMP_POLICY_YEARS = 2;
const LONG_PRESS_MS = 550;
const DEFAULT_VIEWPORT_WEEKS = 1;

/** Wider spacing (~fewer day rows per screen height). */
const DAY_GAP_LOOSE = MIN_DAY_GAP;
/** Tighter spacing (~more future/past days visible without scrolling). */
const DAY_GAP_COMPACT = 30;

/** Vertical distance between day markers; lower packs more days into the same viewport height. */
function effectiveDayGap(viewportWeeks) {
  return viewportWeeks === 2 ? DAY_GAP_COMPACT : DAY_GAP_LOOSE;
}

/**
 * Usable height U and content height so day gap G fits P past + F future.
 * When both past and future days exist, today sits at P/(P+F) so bands stay balanced (avoids a huge empty tail).
 */
function computeScrollTimelineGeometry(pastCount, futureCount, viewportInnerH, viewportWeeks) {
  const P = pastCount;
  const F = futureCount;
  const N = P + 1 + F;
  const pastFrac = P > 0 && F > 0 ? P / (P + F) : PAST_FRAC;
  const G = effectiveDayGap(viewportWeeks);
  const padBottom = Math.round(G * 0.5);
  const viewportUsableH = Math.max(0, viewportInnerH - TIMELINE_PAD_TOP - padBottom);
  let U = viewportUsableH;
  if (N > 1) {
    const m = U_STACK_MARGIN;
    let uMin = 0;
    if (P > 0) uMin = Math.max(uMin, (P * G + m) / pastFrac);
    if (F > 0) uMin = Math.max(uMin, (F * G + m) / (1 - pastFrac));
    U = Math.max(uMin, 1);
  }
  const contentH = TIMELINE_PAD_TOP + U + padBottom;
  const ySplit = TIMELINE_PAD_TOP + pastFrac * U;
  return { U, contentH, ySplit, G, P, F, N, viewportUsableH, pastFrac };
}

/** Open: label clears full rail (right-4 + w-10) + gap. */
const OPEN_LABEL_RIGHT = 'calc(1rem + 2.5rem + 0.375rem)';
/**
 * Horizontal center of the vertical rail inside the **timeline** scroll area (`px-1` on parent):
 * `right-4` (1rem) + half of `w-10` (1.25rem) from that area’s right edge.
 */
const OPEN_LINE_LEFT = 'calc(100% - 1rem - 1.25rem)';
/** Legacy class name kept defined so HMR / older chunks cannot throw; peek/open headers no longer use a bottom rule. */
const TRAINING_LOG_HEADER_CHROME_BORDER_CLASS = 'border-b border-border/45';
/** Peek header cap: `h-9` today control; band ends this far above its bottom edge (15% of button height). */
const PEEK_HEADER_CAP_BOTTOM_INSET = 'calc(0.15 * 2.25rem)';
/** Peek + wide dock: match `WidgetContainer`; viewport gutters handled via fixed `top` / `right` / `bottom`. */
const TRAINING_LOG_DOCK_WIDGET_SURFACE_CLASS =
  'rounded-xl border border-border/45 bg-card/80 shadow-sm backdrop-blur-md ring-1 ring-inset ring-primary/5';
/** Bottom controls `h-9` = 36px; entry dots = 60%. Today ring slightly larger than entries. */
const SETTINGS_CIRCLE_PX = 36;
const DOT_VISUAL_PX = Math.round(SETTINGS_CIRCLE_PX * 0.6);
const TODAY_MARK_PX = Math.round(SETTINGS_CIRCLE_PX * 0.78);
const EMPTY_DOT_PX = Math.max(4, Math.round(DOT_VISUAL_PX * 0.5));
/** Beyond this many calendar slots, only days with entries get Y positions (avoids unusable packing). */
const FULL_CALENDAR_LAYOUT_MAX_DAYS = 150;
/**
 * After padding, sparse layout still gets scrollable rails through empty parts of the loaded interval
 * (otherwise scroll height ends at the last entry and you cannot scroll back toward today/future).
 */
const SPARSE_LAYOUT_PAD_MAX = 500;

/** Dashed vertical strip using ACWR color (training readiness on timeline). */
function acwrPastDashStyle(acwr) {
  const c = acwrToHeatmapColor(acwr ?? null);
  return {
    background: `repeating-linear-gradient(to bottom, ${c} 0px, ${c} 5px, transparent 5px, transparent 10px)`,
  };
}

const COLOR_MAP = {
  primary: 'bg-primary text-primary-foreground border-primary',
  secondary: 'bg-secondary text-secondary-foreground border-secondary',
  accent: 'bg-accent text-accent-foreground border-border',
  destructive: 'bg-destructive text-destructive-foreground border-destructive',
  muted: 'bg-muted text-muted-foreground border-border',
};

const TYPE_LABEL_KEYS = {
  journal: 'trainingLog.typeJournal',
  activity: 'trainingLog.typeActivity',
  planned_session: 'trainingLog.typePlannedSession',
  race: 'trainingLog.typeRace',
  goal: 'trainingLog.typeGoal',
  daily_brief: 'trainingLog.typeDailyBrief',
  health_new_issue: 'trainingLog.typeHealthNewIssue',
  health_follow_up: 'trainingLog.typeHealthFollowUp',
};

function kebabToPascal(s) {
  if (!s) return 'Circle';
  return s
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

function TypeIcon({ iconKey, className }) {
  const name = kebabToPascal(iconKey || 'circle');
  const Lucide = LucideIcons[name] || LucideIcons.Circle;
  return <Lucide className={className} />;
}

function parseItemMetadata(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function dayKeyFromItem(item) {
  return format(new Date(item.occurred_at), 'yyyy-MM-dd');
}

/** Open timeline row: "Tue 6 Nov"; if not this calendar year, append "'25"-style year. */
function formatTimelineClusterRowDate(occurredAt, locale) {
  const d = new Date(occurredAt);
  const y = startOfDay(d).getFullYear();
  const cy = startOfDay(new Date()).getFullYear();
  const base = format(d, 'EEE d MMM', { locale });
  if (y === cy) return base;
  return `${base} '${format(d, 'yy', { locale })}`;
}

/** Absolute calendar distance between two `yyyy-MM-dd` keys (whole days). */
function calendarDayDistanceFromKeys(dayKeyA, dayKeyB) {
  const a = parseISO(`${dayKeyA}T12:00:00`);
  const b = parseISO(`${dayKeyB}T12:00:00`);
  return Math.abs(differenceInCalendarDays(a, b));
}

/** Calendar days strictly before / after today (today sits on the split ring, not in either band). */
function splitStrictPastFuture(sortedDayKeys, todayKey) {
  const past = [];
  const fut = [];
  for (const dk of sortedDayKeys) {
    if (dk < todayKey) past.push(dk);
    else if (dk > todayKey) fut.push(dk);
  }
  return { past, fut };
}

/** Inclusive calendar interval for fetch + scroll buffer (fetch + layout must match). */
function getDefaultTimelineIntervalBounds(nowMs) {
  const todayStart = startOfDay(new Date(nowMs));
  const r = TIMELINE_LOAD_RADIUS_DAYS;
  return {
    start: subDays(todayStart, r),
    end: addDays(todayStart, r),
  };
}

function calendarBandFromIntervalBounds(intervalBounds, nowMs) {
  const todayKey = format(startOfDay(new Date(nowMs)), 'yyyy-MM-dd');
  const allDays = eachDayOfInterval(intervalBounds).map((d) => format(d, 'yyyy-MM-dd'));
  return { ...splitStrictPastFuture(allDays, todayKey), todayKey };
}

function buildTimelineLayout(items, nowMs, innerHeight, intervalBounds, viewportWeeks) {
  const calendarBand = calendarBandFromIntervalBounds(intervalBounds, nowMs);
  const { todayKey } = calendarBand;
  const totalSlots = calendarBand.past.length + calendarBand.fut.length + 1;

  let pastDays;
  let futDays;
  if (totalSlots > FULL_CALENDAR_LAYOUT_MAX_DAYS) {
    const pastDks = new Set();
    const futDks = new Set();
    for (const item of items) {
      const dk = dayKeyFromItem(item);
      if (dk < todayKey) pastDks.add(dk);
      else if (dk > todayKey) futDks.add(dk);
    }
    pastDays = [...pastDks].sort();
    futDays = [...futDks].sort();

    const intervalStart = startOfDay(intervalBounds.start);
    const intervalEnd = startOfDay(intervalBounds.end);
    const todayStart = startOfDay(new Date(nowMs));

    const mergedPast = new Set(pastDays);
    const firstPastItem = pastDays[0];
    const pastStopExclusive = firstPastItem
      ? startOfDay(parseISO(`${firstPastItem}T12:00:00`))
      : todayStart;
    let wPast = intervalStart;
    let addedPast = 0;
    while (wPast < pastStopExclusive && addedPast < SPARSE_LAYOUT_PAD_MAX) {
      const k = format(wPast, 'yyyy-MM-dd');
      if (k < todayKey) mergedPast.add(k);
      wPast = addDays(wPast, 1);
      addedPast += 1;
    }
    pastDays = [...mergedPast].sort();

    const mergedFut = new Set(futDays);
    const lastFutItem = futDays.length > 0 ? futDays[futDays.length - 1] : todayKey;
    let wFut = addDays(startOfDay(parseISO(`${lastFutItem}T12:00:00`)), 1);
    let addedFut = 0;
    while (wFut <= intervalEnd && addedFut < SPARSE_LAYOUT_PAD_MAX) {
      const k = format(wFut, 'yyyy-MM-dd');
      if (k > todayKey) mergedFut.add(k);
      wFut = addDays(wFut, 1);
      addedFut += 1;
    }
    futDays = [...mergedFut].sort();
  } else {
    pastDays = calendarBand.past;
    futDays = calendarBand.fut;
  }

  const P = pastDays.length;
  const vw = viewportWeeks === 2 ? 2 : 1;
  const geo = computeScrollTimelineGeometry(P, futDays.length, innerHeight, vw);
  const { contentH, ySplit, G, pastFrac } = geo;

  const ordered = [...pastDays, todayKey, ...futDays];
  const yByDay = new Map();
  ordered.forEach((dk, i) => {
    yByDay.set(dk, ySplit + (i - P) * G);
  });

  if (innerHeight <= 0) {
    return { laidOut: [], contentH: 0, yByDay, pastDays, futDays, todayKey, ySplit, pastFrac };
  }

  if (!items.length) {
    return { laidOut: [], contentH, yByDay, pastDays, futDays, todayKey, ySplit, pastFrac };
  }

  const laidOut = items.map((item) => {
    const t = new Date(item.occurred_at).getTime();
    const dk = dayKeyFromItem(item);
    const y = yByDay.has(dk) ? yByDay.get(dk) : ySplit;
    return { item, y, t };
  });
  return { laidOut, contentH, yByDay, pastDays, futDays, todayKey, ySplit, pastFrac };
}

function buildEmptyDayMarkers(yByDay, daysWithPosts, todayKey) {
  const markers = [];
  for (const dk of yByDay.keys()) {
    if (dk === todayKey || daysWithPosts.has(dk)) continue;
    markers.push({ dayKey: dk, y: yByDay.get(dk) });
  }
  return markers.sort((a, b) => a.y - b.y);
}

/** One cluster per calendar day (same-day entries share one dot + count). */
function clusterByDay(laidOut) {
  if (!laidOut.length) return [];
  const map = new Map();
  for (const x of laidOut) {
    const dk = dayKeyFromItem(x.item);
    if (!map.has(dk)) map.set(dk, []);
    map.get(dk).push(x);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, arr]) => {
      const yAvg = arr.reduce((s, z) => s + z.y, 0) / arr.length;
      const primary = [...arr].sort((a, b) => b.t - a.t)[0];
      return { y: yAvg, items: arr.map((z) => z.item), primary: primary.item, count: arr.length };
    });
}

const TrainingLogDock = forwardRef(function TrainingLogDock({ onInsetChange }, ref) {
  const { t, language } = useLanguage();
  const locale = language === 'no' ? nb : enUS;
  const saveTimer = useRef(null);
  const logPrefsRef = useRef({
    panelMode: 'hidden',
    visibleTypeKeys: null,
    timelineViewportWeeks: DEFAULT_VIEWPORT_WEEKS,
    showAcwrLine: false,
  });
  const [logPrefs, setLogPrefs] = useState(() => ({ ...logPrefsRef.current }));

  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [loading, setLoading] = useState(false);
  const [searchRemoteMiss, setSearchRemoteMiss] = useState(false);
  /** True after Enter when resolving search (wide API and/or timeline reload). */
  const [searchInProgress, setSearchInProgress] = useState(false);

  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [dayModalClusterItems, setDayModalClusterItems] = useState([]);
  const [plannerOpen, setPlannerOpen] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  /** yyyy-MM-dd for new entry / quick note; null = today */
  const [composeTargetDayKey, setComposeTargetDayKey] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [logTypes, setLogTypes] = useState([]);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeType, setComposeType] = useState('journal');
  const [composeSaving, setComposeSaving] = useState(false);
  const [composePickedSession, setComposePickedSession] = useState(null);
  const [composeHealthIssueId, setComposeHealthIssueId] = useState(null);
  const [composeOpenIssues, setComposeOpenIssues] = useState([]);
  const [composeSessionPickerOpen, setComposeSessionPickerOpen] = useState(false);

  /** Loaded calendar interval (inclusive start/end, start-of-day); drives fetch + layout. */
  const [loadedInterval, setLoadedInterval] = useState(() => getDefaultTimelineIntervalBounds(Date.now()));
  const loadedIntervalRef = useRef(loadedInterval);
  loadedIntervalRef.current = loadedInterval;
  /** Skip resetting interval on first mount (useState already defaults); only reset when user changes density. */
  const skipViewportWeeksIntervalResetRef = useRef(true);
  const timelineExtendingRef = useRef(false);
  /** After `/items` finishes, re-check scroll position to chain another edge extend without waiting for scroll input. */
  const prevLoadingForTimelineExtendRef = useRef(false);
  const pendingScrollAdjustRef = useRef(null);
  /** Monotonic id so an older /items response cannot overwrite a newer interval fetch. */
  const loadItemsSeqRef = useRef(0);
  const [itemDateBounds, setItemDateBounds] = useState(null);

  /** Applied on Enter in open search; filters timeline and runs scroll-to-hit. */
  const [committedTimelineSearch, setCommittedTimelineSearch] = useState('');
  /** Bumps when search is submitted so scroll-to-hit layout effect re-runs even if query unchanged. */
  const [timelineSearchSubmitGen, setTimelineSearchSubmitGen] = useState(0);
  const timelineSearchSubmitGenRef = useRef(timelineSearchSubmitGen);
  timelineSearchSubmitGenRef.current = timelineSearchSubmitGen;

  const [jumpDateOpen, setJumpDateOpen] = useState(false);
  const [jumpDateInput, setJumpDateInput] = useState(() => format(startOfDay(new Date()), 'yyyy-MM-dd'));
  const jumpDateInputRef = useRef(null);
  const [pendingScrollDayKey, setPendingScrollDayKey] = useState(null);
  const pendingScrollDayKeyRef = useRef(null);
  pendingScrollDayKeyRef.current = pendingScrollDayKey;
  /** One-shot: mirror onTodayClick after first data paint so interval + scroll match that path (fixes init layout/scroll). */
  const didInitialTodaySyncRef = useRef(false);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const [acwrTimeline, setAcwrTimeline] = useState({
    entries: [],
    error: null,
  });

  const timelineScrollRef = useRef(null);
  /** Min/max cluster `y` for content-aware prefetch (refreshed in layout when `clusters` changes). */
  const clusterYBoundsRef = useRef({ minY: null, maxY: null });
  const railColumnRef = useRef(null);
  const dockAsideRef = useRef(null);
  const [innerH, setInnerH] = useState(400);
  const panelMode = logPrefs.panelMode;
  const panelModeRef = useRef(panelMode);
  panelModeRef.current = panelMode;
  const panelWidth = panelMode === 'open' ? OPEN_W : panelMode === 'peek' ? PEEK_W : 0;

  useEffect(() => {
    if (panelMode === 'hidden') {
      setCommittedTimelineSearch('');
      setSearchRemoteMiss(false);
      setSearchInProgress(false);
    }
  }, [panelMode]);

  const onTimelineSearchCommitted = useCallback((v) => {
    setSearchRemoteMiss(false);
    const next = typeof v === 'string' ? v : '';
    setCommittedTimelineSearch(next);
    setTimelineSearchSubmitGen((g) => g + 1);
    const q = next.trim();
    const qLower = q.toLowerCase();
    if (!q) {
      setSearchInProgress(false);
      return;
    }
    const instantMatch = itemsRef.current.some((i) => itemMatchesSearchLower(i, qLower));
    setSearchInProgress(!instantMatch);
  }, []);

  const flushLogPrefs = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put('/api/users/settings', { trainingLogPreferences: { ...logPrefsRef.current } }).catch(() => {});
    }, 400);
  }, []);

  /** Re-sync scroll-port height after layout mode / spring width — stale innerH collapses timeline geometry and kills scrolling. */
  const bumpScrollPortHeight = useCallback(() => {
    requestAnimationFrame(() => {
      const el = timelineScrollRef.current;
      if (!el || panelModeRef.current === 'hidden') return;
      const h = el.clientHeight;
      if (h > 0) setInnerH((prev) => (Math.abs(prev - h) < 2 ? prev : h));
    });
  }, []);

  const updateLogPrefs = useCallback(
    (patch) => {
      logPrefsRef.current = { ...logPrefsRef.current, ...patch };
      setLogPrefs({ ...logPrefsRef.current });
      flushLogPrefs();
    },
    [flushLogPrefs]
  );

  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return undefined;
    const read = () => {
      const h = el.clientHeight;
      if (h > 0) setInnerH(h);
    };
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => requestAnimationFrame(read));
    });
    ro.observe(el);
    read();
    requestAnimationFrame(() => requestAnimationFrame(read));
    return () => ro.disconnect();
  }, [panelMode]);

  /** After prefs/items paint, flex height can differ from the first ResizeObserver tick — sync before layout uses innerH (min track height, scroll balance). */
  useLayoutEffect(() => {
    if (panelMode === 'hidden' || !prefsLoaded) return;
    const el = timelineScrollRef.current;
    if (!el) return;
    const read = () => {
      const h = el.clientHeight;
      if (h > 0) setInnerH((prev) => (Math.abs(prev - h) < 2 ? prev : h));
    };
    read();
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(read);
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [panelMode, prefsLoaded]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/users/settings');
        const p = res.data?.trainingLogPreferences;
        if (!cancelled && p) {
          let vw = p.timelineViewportWeeks === 2 ? 2 : 1;
          if (p.timelineViewportWeeks !== 1 && p.timelineViewportWeeks !== 2) {
            vw = p.timelineRange === '2w' ? 2 : 1;
          }
          const next = {
            panelMode: ['hidden', 'peek', 'open'].includes(p.panelMode) ? p.panelMode : 'hidden',
            visibleTypeKeys:
              p.visibleTypeKeys !== undefined && Array.isArray(p.visibleTypeKeys)
                ? p.visibleTypeKeys
                : null,
            timelineViewportWeeks: vw,
            showAcwrLine: p.showAcwrLine === true,
          };
          logPrefsRef.current = next;
          setLogPrefs(next);
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setPrefsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchAcwrTimeline = useCallback(() => {
    const { start, end } = loadedIntervalRef.current;
    const todayK = format(startOfDay(new Date()), 'yyyy-MM-dd');
    api
      .get('/api/training-log/timeline-acwr', {
        params: {
          today: todayK,
          from: format(startOfDay(start), 'yyyy-MM-dd'),
          to: format(startOfDay(end), 'yyyy-MM-dd'),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        },
      })
      .then((res) => {
        setAcwrTimeline({
          entries: res.data?.entries || [],
          error: res.data?.error || null,
        });
      })
      .catch(() => {
        setAcwrTimeline({ entries: [], error: null });
      });
  }, []);

  const loadItems = useCallback(async () => {
    const seq = ++loadItemsSeqRef.current;
    const { start, end } = loadedIntervalRef.current;
    setLoading(true);
    try {
      const res = await api.get('/api/training-log/items', {
        params: {
          from: start.toISOString(),
          to: end.toISOString(),
          limit: 6000,
        },
      });
      if (seq !== loadItemsSeqRef.current) return;
      const rows = (res.data?.items || []).map((row) => ({
        ...row,
        metadata: parseItemMetadata(row.metadata),
      }));
      setItems(rows);
    } catch (e) {
      console.error(e);
      if (seq !== loadItemsSeqRef.current) return;
      setItems([]);
    } finally {
      if (seq === loadItemsSeqRef.current) {
        setLoading(false);
        timelineExtendingRef.current = false;
      }
    }
  }, []);

  /** Re-center fetch window when density changes (also recomputes full-calendar vs sparse layout). */
  useEffect(() => {
    if (skipViewportWeeksIntervalResetRef.current) {
      skipViewportWeeksIntervalResetRef.current = false;
      return;
    }
    setLoadedInterval(getDefaultTimelineIntervalBounds(Date.now()));
  }, [logPrefs.timelineViewportWeeks]);

  useEffect(() => {
    if (!prefsLoaded || panelMode === 'hidden') return;
    let cancelled = false;
    api
      .get('/api/training-log/items-date-bounds')
      .then((res) => {
        if (!cancelled) setItemDateBounds(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setItemDateBounds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [prefsLoaded, panelMode]);

  useEffect(() => {
    if (panelMode === 'hidden') return undefined;
    const onSync = () => {
      api
        .get('/api/training-log/items-date-bounds')
        .then((res) => setItemDateBounds(res.data || null))
        .catch(() => setItemDateBounds(null));
    };
    window.addEventListener('stravaSyncComplete', onSync);
    return () => window.removeEventListener('stravaSyncComplete', onSync);
  }, [panelMode]);

  useEffect(() => {
    if (panelMode === 'hidden') return;
    loadItems();
  }, [panelMode, loadedInterval, loadItems]);

  useEffect(() => {
    if (panelMode === 'hidden') return undefined;
    const onTrainingUpdated = () => {
      loadItems();
    };
    window.addEventListener('trainingUpdated', onTrainingUpdated);
    return () => window.removeEventListener('trainingUpdated', onTrainingUpdated);
  }, [panelMode, loadItems]);

  useEffect(() => {
    if (!onInsetChange) return;
    if (panelMode === 'hidden') onInsetChange(TRAINING_LOG_DOCK_HIDDEN_RESERVE_PX);
    else if (panelMode === 'peek') onInsetChange(PEEK_W);
    else onInsetChange(OPEN_W);
  }, [panelMode, onInsetChange]);

  useEffect(() => {
    if (panelMode === 'hidden' || loading) return;
    if (!logPrefs.showAcwrLine) {
      setAcwrTimeline({ entries: [], error: null });
      return;
    }
    fetchAcwrTimeline();
  }, [panelMode, loading, items, fetchAcwrTimeline, logPrefs.showAcwrLine, loadedInterval]);

  useEffect(() => {
    if (!settingsOpen) return;
    api
      .get('/api/training-log/types')
      .then((res) => setLogTypes(res.data?.types || []))
      .catch(() => setLogTypes([]));
  }, [settingsOpen]);

  const cycleMode = useCallback(() => {
    const order = ['hidden', 'peek', 'open'];
    const i = order.indexOf(logPrefsRef.current.panelMode);
    const next = order[(i + 1) % order.length];
    updateLogPrefs({ panelMode: next });
  }, [updateLogPrefs]);

  useImperativeHandle(
    ref,
    () => ({
      cycleMode,
      setMode: (m) => {
        if (['hidden', 'peek', 'open'].includes(m)) {
          updateLogPrefs({ panelMode: m });
        }
      },
    }),
    [cycleMode, updateLogPrefs]
  );

  const filteredItems = useMemo(() => {
    const keys = logPrefs.visibleTypeKeys;
    if (keys === null || keys === undefined) return items;
    if (keys.length === 0) return [];
    const set = new Set(keys);
    return items.filter((i) => set.has(i.type_key));
  }, [items, logPrefs.visibleTypeKeys]);

  const committedSearchTrimmed = committedTimelineSearch.trim();
  const committedSearchLower = committedSearchTrimmed.toLowerCase();

  const matchingItemsForSearch = useMemo(() => {
    if (!committedSearchLower) return [];
    return items.filter((i) => itemMatchesSearchLower(i, committedSearchLower));
  }, [items, committedSearchLower]);

  useEffect(() => {
    if (!searchInProgress) return;
    if (!committedSearchTrimmed) return;
    if (searchRemoteMiss) {
      setSearchInProgress(false);
      return;
    }
    if (matchingItemsForSearch.length > 0 && !loading) {
      setSearchInProgress(false);
    }
  }, [
    searchInProgress,
    committedSearchTrimmed,
    searchRemoteMiss,
    matchingItemsForSearch.length,
    loading,
  ]);

  /**
   * Full timeline for scroll geometry: always show the normal type-filtered set, plus any
   * search hits that were hidden by type prefs (e.g. Strava rows when Activity is off).
   */
  const displayItemsForLayout = useMemo(() => {
    if (!committedSearchLower) return filteredItems;
    const byId = new Map();
    for (const i of filteredItems) byId.set(i.id, i);
    for (const i of matchingItemsForSearch) byId.set(i.id, i);
    return Array.from(byId.values());
  }, [filteredItems, matchingItemsForSearch, committedSearchLower]);

  const nowMs = Date.now();
  const viewportWeeks = logPrefs.timelineViewportWeeks === 2 ? 2 : 1;

  const jumpPolicyMin = useMemo(() => {
    const t = startOfDay(new Date());
    const policyEarliest = subYears(t, JUMP_POLICY_YEARS);
    if (!itemDateBounds?.minOccurredAt) return policyEarliest;
    const oldest = startOfDay(new Date(itemDateBounds.minOccurredAt));
    return minDate([oldest, policyEarliest]);
  }, [itemDateBounds?.minOccurredAt]);

  const jumpPolicyMax = useMemo(() => {
    const t = startOfDay(new Date());
    const policyLatest = startOfDay(addYears(t, JUMP_POLICY_YEARS));
    if (!itemDateBounds?.maxOccurredAt) return policyLatest;
    const latest = startOfDay(new Date(itemDateBounds.maxOccurredAt));
    return maxDate([latest, policyLatest]);
  }, [itemDateBounds?.maxOccurredAt]);

  const jumpPolicyMinRef = useRef(jumpPolicyMin);
  const jumpPolicyMaxRef = useRef(jumpPolicyMax);
  jumpPolicyMinRef.current = jumpPolicyMin;
  jumpPolicyMaxRef.current = jumpPolicyMax;

  const evaluateTimelineScrollExtend = useCallback(() => {
    const el = timelineScrollRef.current;
    if (!el || panelModeRef.current === 'hidden') return;
    if (timelineExtendingRef.current) return;
    const interval = loadedIntervalRef.current;
    const pMin = jumpPolicyMinRef.current;
    const pMax = jumpPolicyMaxRef.current;
    if (!interval?.start || !pMin || !pMax) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    const edgePx = computeScrollEdgePx(clientHeight, maxScroll);
    const { minY } = clusterYBoundsRef.current;
    const gapLead = clusterGapLeadPx(clientHeight);
    const gapAboveFirst =
      minY != null && Number.isFinite(minY) ? minY - scrollTop : Number.NEGATIVE_INFINITY;
    const towardPast =
      scrollTop < edgePx || gapAboveFirst > gapLead;
    /**
     * Future extend: scroll position only. Do not use “gap below last item cluster”: after sparse
     * tail padding, empty days sit below the last entry but are already inside `loadedInterval`;
     * that gap stayed large and re-triggered `/items` in a loop until policy max.
     */
    const towardFuture = scrollTop > maxScroll - edgePx;

    if (maxScroll > 0 && towardPast && interval.start.getTime() > pMin.getTime()) {
      const newStart = startOfDay(maxDate([subDays(interval.start, EXTEND_CHUNK_DAYS), pMin]));
      if (newStart.getTime() >= interval.start.getTime()) return;
      timelineExtendingRef.current = true;
      pendingScrollAdjustRef.current = { prevScrollHeight: scrollHeight };
      setLoadedInterval((prev) => ({ ...prev, start: newStart }));
      return;
    }
    if (maxScroll > 0 && towardFuture && interval.end.getTime() < pMax.getTime()) {
      const newEnd = startOfDay(minDate([addDays(interval.end, EXTEND_CHUNK_DAYS), pMax]));
      if (newEnd.getTime() <= interval.end.getTime()) return;
      timelineExtendingRef.current = true;
      setLoadedInterval((prev) => ({ ...prev, end: newEnd }));
    }
  }, []);

  /**
   * Windowed `/items` may omit matches. If committed query has no local hits, query API with `search`
   * over policy range, then widen `loadedInterval` around hit dates so a follow-up load fills the timeline.
   */
  useEffect(() => {
    if (panelMode === 'hidden' || !prefsLoaded) return;
    const q = committedTimelineSearch.trim();
    if (!q) return;

    const qLower = q.toLowerCase();
    const local = itemsRef.current.filter((i) => itemMatchesSearchLower(i, qLower));
    if (local.length > 0) return;

    const genAtStart = timelineSearchSubmitGenRef.current;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/training-log/items', {
          params: {
            from: jumpPolicyMin.toISOString(),
            to: jumpPolicyMax.toISOString(),
            search: q,
            limit: 800,
          },
        });
        if (cancelled || genAtStart !== timelineSearchSubmitGenRef.current) return;
        const hits = (res.data?.items || []).map((row) => ({
          ...row,
          metadata: parseItemMetadata(row.metadata),
        }));
        if (hits.length === 0) {
          if (genAtStart !== timelineSearchSubmitGenRef.current) return;
          setSearchRemoteMiss(true);
          setSearchInProgress(false);
          return;
        }
        let minMs = Infinity;
        let maxMs = -Infinity;
        for (const h of hits) {
          const tm = new Date(h.occurred_at).getTime();
          if (tm < minMs) minMs = tm;
          if (tm > maxMs) maxMs = tm;
        }
        if (!Number.isFinite(minMs)) {
          if (genAtStart === timelineSearchSubmitGenRef.current) setSearchInProgress(false);
          return;
        }
        const hitStart = startOfDay(new Date(minMs));
        const hitEnd = startOfDay(new Date(maxMs));
        const r = TIMELINE_LOAD_RADIUS_DAYS;
        const nextStart = startOfDay(subDays(hitStart, r));
        const nextEnd = startOfDay(addDays(hitEnd, r));
        const c = clampIntervalToPolicy(nextStart, nextEnd, jumpPolicyMin, jumpPolicyMax);
        if (genAtStart !== timelineSearchSubmitGenRef.current) return;
        setLoadedInterval({ start: c.start, end: c.end });
      } catch (e) {
        console.error(e);
        if (!cancelled && genAtStart === timelineSearchSubmitGenRef.current) {
          setSearchRemoteMiss(true);
          setSearchInProgress(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    prefsLoaded,
    panelMode,
    committedTimelineSearch,
    timelineSearchSubmitGen,
    jumpPolicyMin,
    jumpPolicyMax,
  ]);

  useEffect(() => {
    if (panelMode === 'hidden' || !prefsLoaded) return;
    setLoadedInterval((prev) => {
      const c = clampIntervalToPolicy(prev.start, prev.end, jumpPolicyMin, jumpPolicyMax);
      if (
        c.start.getTime() === prev.start.getTime() &&
        c.end.getTime() === prev.end.getTime()
      ) {
        return prev;
      }
      return { start: startOfDay(c.start), end: startOfDay(c.end) };
    });
  }, [jumpPolicyMin, jumpPolicyMax, panelMode, prefsLoaded]);

  const timelineLayout = useMemo(
    () => buildTimelineLayout(displayItemsForLayout, nowMs, innerH, loadedInterval, viewportWeeks),
    [displayItemsForLayout, nowMs, innerH, loadedInterval, viewportWeeks]
  );
  const {
    laidOut,
    contentH,
    yByDay,
    todayKey: layoutTodayKey,
    ySplit,
    pastFrac: timelinePastFrac,
    pastDays: layoutPastDays,
    futDays: layoutFutDays,
  } = timelineLayout;

  const orderedDayKeys = useMemo(
    () => [...layoutPastDays, layoutTodayKey, ...layoutFutDays],
    [layoutPastDays, layoutTodayKey, layoutFutDays]
  );

  const acwrByDate = useMemo(() => {
    const m = new Map();
    for (const e of acwrTimeline.entries || []) {
      if (e?.date) m.set(e.date, e.acwr ?? null);
    }
    return m;
  }, [acwrTimeline.entries]);

  const acwrSegments = useMemo(() => {
    if (!logPrefs.showAcwrLine || orderedDayKeys.length === 0) return [];
    const segs = [];
    for (let i = 0; i < orderedDayKeys.length; i++) {
      const dk = orderedDayKeys[i];
      const yCur = yByDay.get(dk);
      if (yCur == null) continue;
      const yPrev = i === 0 ? 0 : yByDay.get(orderedDayKeys[i - 1]);
      if (i > 0 && yPrev == null) continue;
      const top = i === 0 ? 0 : yPrev;
      const height = Math.max(1, yCur - top);
      const acwr = acwrByDate.get(dk) ?? null;
      const dashed = dk < layoutTodayKey;
      segs.push({ key: `acwr-${dk}`, top, height, acwr, dashed, tail: false });
    }
    const lastDk = orderedDayKeys[orderedDayKeys.length - 1];
    const yLast = yByDay.get(lastDk);
    if (yLast != null) {
      segs.push({
        key: `acwr-tail-${lastDk}`,
        top: yLast,
        height: 0,
        acwr: acwrByDate.get(lastDk) ?? null,
        dashed: lastDk < layoutTodayKey,
        tail: true,
      });
    }
    return segs;
  }, [logPrefs.showAcwrLine, orderedDayKeys, yByDay, acwrByDate, layoutTodayKey]);
  /** At least layout height; `max(100%, …)` fills the scrollport when geometry is shorter (sparse days / flex collapse). */
  const timelineTrackMinStyle = useMemo(
    () => ({ minHeight: `max(100%, ${Math.max(1, Math.round(contentH))}px)` }),
    [contentH]
  );
  const clusters = useMemo(() => clusterByDay(laidOut), [laidOut]);

  useLayoutEffect(() => {
    if (!clusters.length) {
      clusterYBoundsRef.current = { minY: null, maxY: null };
      return;
    }
    let minY = Infinity;
    let maxY = -Infinity;
    for (const c of clusters) {
      if (c.y < minY) minY = c.y;
      if (c.y > maxY) maxY = c.y;
    }
    clusterYBoundsRef.current = { minY, maxY };
  }, [clusters]);

  /** When this changes, search hits or timeline geometry changed — re-check scroll-to-nearest-hit. */
  const searchHitScrollSig = useMemo(() => {
    if (panelMode !== 'open') return '';
    if (!committedSearchTrimmed || matchingItemsForSearch.length === 0) return '';
    const ids = matchingItemsForSearch.map((i) => i.id).join(',');
    return `${committedSearchTrimmed}|${ids}|${innerH}|${Math.round(contentH)}|${layoutTodayKey}|${timelineSearchSubmitGen}`;
  }, [
    panelMode,
    committedSearchTrimmed,
    matchingItemsForSearch,
    innerH,
    contentH,
    layoutTodayKey,
    timelineSearchSubmitGen,
  ]);

  const daysWithPosts = useMemo(
    () => new Set(displayItemsForLayout.map((i) => dayKeyFromItem(i))),
    [displayItemsForLayout]
  );

  const emptyDayMarkers = useMemo(
    () => buildEmptyDayMarkers(yByDay, daysWithPosts, layoutTodayKey),
    [yByDay, daysWithPosts, layoutTodayKey]
  );

  /** Latest layout metrics for scroll positioning (avoid effect deps that fire every load/resize). */
  const layoutMetricsRef = useRef({
    innerH: 0,
    ySplit: 0,
    timelinePastFrac: PAST_FRAC,
    contentH: 0,
  });
  layoutMetricsRef.current = { innerH, ySplit, timelinePastFrac, contentH };

  const hadTimelineItemsRef = useRef(false);
  const prevPanelModeForBalanceRef = useRef(panelMode);
  const prevViewportWeeksForBalanceRef = useRef(viewportWeeks);
  /** Reset when panel hides so reopen + ResizeObserver get a fresh height pass. */
  const prevInnerHForBalanceRef = useRef(0);

  useEffect(() => {
    if (panelMode === 'hidden') {
      hadTimelineItemsRef.current = false;
      prevInnerHForBalanceRef.current = 0;
    }
  }, [panelMode]);

  /** Balance scroll only when opening/changing panel, density, first item paint, or viewport height settles — not on every fetch/content height. */
  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el || panelMode === 'hidden') return;

    const nowHave = filteredItems.length > 0;
    const firstPaint = nowHave && !hadTimelineItemsRef.current;

    const panelChanged = prevPanelModeForBalanceRef.current !== panelMode;
    const vwChanged = prevViewportWeeksForBalanceRef.current !== viewportWeeks;
    prevPanelModeForBalanceRef.current = panelMode;
    prevViewportWeeksForBalanceRef.current = viewportWeeks;

    const prevIH = prevInnerHForBalanceRef.current;
    const firstInnerMeasure = prevIH <= 0 && innerH > 0;
    const innerSized =
      firstInnerMeasure ||
      (prevIH > 0 &&
        innerH > 0 &&
        Math.abs(innerH - prevIH) >= Math.max(24, prevIH * 0.08));
    prevInnerHForBalanceRef.current = innerH;

    if (pendingScrollDayKey) return;

    if (!firstPaint && !panelChanged && !vwChanged && !innerSized) return;

    const applyBalance = () => {
      const scrollEl = timelineScrollRef.current;
      if (!scrollEl || panelModeRef.current === 'hidden' || pendingScrollDayKeyRef.current) return;
      const { ySplit: ys, timelinePastFrac: tf, contentH: ch } = layoutMetricsRef.current;
      const vh = scrollEl.clientHeight;
      if (vh <= 0 || ch <= 0) return;
      if (scrollEl.scrollHeight < 4) return;
      const maxScroll = Math.max(0, scrollEl.scrollHeight - vh);
      const desired = ys - vh * tf;
      scrollEl.scrollTop = Math.max(0, Math.min(maxScroll, desired));
      if (nowHave) hadTimelineItemsRef.current = true;
      evaluateTimelineScrollExtend();
    };

    requestAnimationFrame(() => requestAnimationFrame(applyBalance));
  }, [filteredItems.length, panelMode, viewportWeeks, innerH, loading, contentH, evaluateTimelineScrollExtend]);

  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el || panelMode === 'hidden') return undefined;
    const onScroll = () => evaluateTimelineScrollExtend();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [panelMode, evaluateTimelineScrollExtend]);

  useLayoutEffect(() => {
    if (panelMode === 'hidden') {
      prevLoadingForTimelineExtendRef.current = loading;
      return;
    }
    const wasLoading = prevLoadingForTimelineExtendRef.current;
    prevLoadingForTimelineExtendRef.current = loading;
    const finishedLoad = wasLoading && !loading;
    if (!finishedLoad) return;
    requestAnimationFrame(() => requestAnimationFrame(() => evaluateTimelineScrollExtend()));
  }, [loading, panelMode, evaluateTimelineScrollExtend]);

  useLayoutEffect(() => {
    if (!pendingScrollDayKey || loading) return;
    const y = yByDay.get(pendingScrollDayKey);
    if (y == null) return;
    const el = timelineScrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = Math.max(0, Math.min(maxScroll, y - DAY_SCROLL_TOP_INSET));
    hadTimelineItemsRef.current = true;
    setPendingScrollDayKey(null);
  }, [pendingScrollDayKey, loading, yByDay]);

  /** Open + active search: if no matching row is in view, jump scroll to the match day closest to today (ties → earlier in result list). */
  useLayoutEffect(() => {
    if (panelMode !== 'open' || loading || pendingScrollDayKey) return;
    if (!searchHitScrollSig) return;
    if (!committedSearchTrimmed || matchingItemsForSearch.length === 0 || clusters.length === 0) return;

    const el = timelineScrollRef.current;
    if (!el || el.clientHeight <= 0) return;

    const vTop = el.scrollTop;
    const vBottom = el.scrollTop + el.clientHeight;
    const rowHalfPx = 22;

    let anyHitVisible = false;
    for (const c of clusters) {
      const hasMatch = c.items.some((it) => itemMatchesSearchLower(it, committedSearchLower));
      if (!hasMatch) continue;
      const y = c.y;
      if (y >= vTop - rowHalfPx && y <= vBottom + rowHalfPx) {
        anyHitVisible = true;
        break;
      }
    }
    if (anyHitVisible) return;

    const firstOccurrenceByDay = new Map();
    matchingItemsForSearch.forEach((it, idx) => {
      const dk = dayKeyFromItem(it);
      if (!firstOccurrenceByDay.has(dk)) firstOccurrenceByDay.set(dk, idx);
    });
    const dayKeys = [...firstOccurrenceByDay.keys()];
    dayKeys.sort((a, b) => {
      const da = calendarDayDistanceFromKeys(a, layoutTodayKey);
      const db = calendarDayDistanceFromKeys(b, layoutTodayKey);
      if (da !== db) return da - db;
      return (firstOccurrenceByDay.get(a) ?? 0) - (firstOccurrenceByDay.get(b) ?? 0);
    });
    const targetDay = dayKeys[0];
    const y = yByDay.get(targetDay);
    if (y == null) return;

    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
    el.scrollTop = Math.max(
      0,
      Math.min(maxScroll, y - DAY_SCROLL_TOP_INSET - OPEN_SEARCH_HIT_SCROLL_EXTRA_TOP_PX)
    );
    hadTimelineItemsRef.current = true;
  }, [panelMode, loading, pendingScrollDayKey, searchHitScrollSig]);

  useLayoutEffect(() => {
    const adj = pendingScrollAdjustRef.current;
    if (adj == null || loading) return;
    const el = timelineScrollRef.current;
    if (el) {
      const delta = el.scrollHeight - adj.prevScrollHeight;
      if (delta !== 0) el.scrollTop += delta;
    }
    pendingScrollAdjustRef.current = null;
  }, [loading, items, contentH]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const onTodayPointerDown = useCallback(() => {
    longPressTriggeredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setJumpDateInput(format(startOfDay(new Date()), 'yyyy-MM-dd'));
      setJumpDateOpen(true);
      clearLongPressTimer();
    }, LONG_PRESS_MS);
  }, [clearLongPressTimer]);

  const onTodayPointerUpCancel = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  /** Reset loaded range to ~2 months around today (windowed fetch; scroll edges load more). */
  const syncTimelineToToday = useCallback(() => {
    setLoadedInterval(getDefaultTimelineIntervalBounds(Date.now()));
    const todayKey = format(startOfDay(new Date()), 'yyyy-MM-dd');
    setPendingScrollDayKey(null);
    queueMicrotask(() => setPendingScrollDayKey(todayKey));
  }, []);

  const onTodayClick = useCallback(
    (e) => {
      if (longPressTriggeredRef.current) {
        e.preventDefault();
        e.stopPropagation();
        longPressTriggeredRef.current = false;
        return;
      }
      syncTimelineToToday();
    },
    [syncTimelineToToday]
  );

  useEffect(() => {
    if (panelMode === 'hidden' || !prefsLoaded || loading || filteredItems.length === 0) return;
    if (didInitialTodaySyncRef.current) return;
    didInitialTodaySyncRef.current = true;
    syncTimelineToToday();
  }, [panelMode, prefsLoaded, loading, filteredItems.length, syncTimelineToToday]);

  const applyJumpToDate = useCallback((rawOverride) => {
    const raw = String(rawOverride ?? jumpDateInput).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return;
    const r = TIMELINE_LOAD_RADIUS_DAYS;
    let target = startOfDay(parseISO(`${raw}T12:00:00`));
    if (target < jumpPolicyMin) target = jumpPolicyMin;
    if (target > jumpPolicyMax) target = jumpPolicyMax;
    const dk = format(target, 'yyyy-MM-dd');
    setJumpDateInput(dk);
    const aroundTarget = {
      start: subDays(target, r),
      end: addDays(target, r),
    };
    const clamped = clampIntervalToPolicy(aroundTarget.start, aroundTarget.end, jumpPolicyMin, jumpPolicyMax);
    setLoadedInterval({
      start: startOfDay(clamped.start),
      end: startOfDay(clamped.end),
    });
    setJumpDateOpen(false);
    setPendingScrollDayKey(dk);
  }, [jumpDateInput, jumpPolicyMin, jumpPolicyMax]);

  useEffect(() => {
    if (!pendingScrollDayKey || loading) return;
    if (yByDay.has(pendingScrollDayKey)) return;
    const id = window.setTimeout(() => setPendingScrollDayKey(null), 150);
    return () => window.clearTimeout(id);
  }, [pendingScrollDayKey, loading, yByDay]);

  const submitCompose = async () => {
    const dayKey = composeTargetDayKey ?? format(new Date(), 'yyyy-MM-dd');
    setComposeSaving(true);
    try {
      let healthIssueId = null;
      if (composeType === 'health_new_issue') {
        const cr = await api.post('/api/health-issues', {
          issue_kind: 'pain',
          title: composeTitle.trim() || t('trainingLog.defaultEntryTitle'),
          notes: composeBody.trim() || null,
          started_at: parseISO(`${dayKey}T12:00:00`).toISOString(),
          functional_impact: 'none',
          sites: [],
        });
        healthIssueId = cr.data?.issue?.id ?? null;
      } else if (composeType === 'health_follow_up') {
        if (!composeHealthIssueId) {
          setComposeSaving(false);
          return;
        }
        healthIssueId = composeHealthIssueId;
      }
      await api.post('/api/training-log/items', {
        typeKey: composeType,
        occurredAt: parseISO(`${dayKey}T12:00:00`).toISOString(),
        title: composeTitle.trim() || t('trainingLog.defaultEntryTitle'),
        body: composeBody.trim() || null,
        ...(composeType === 'planned_session' && composePickedSession?.id
          ? { trainingSessionId: composePickedSession.id }
          : {}),
        ...(healthIssueId ? { healthIssueId } : {}),
      });
      setComposeOpen(false);
      setComposeTargetDayKey(null);
      setComposeTitle('');
      setComposeBody('');
      setComposeType('journal');
      setComposePickedSession(null);
      setComposeHealthIssueId(null);
      await loadItems();
    } catch (e) {
      console.error(e);
    } finally {
      setComposeSaving(false);
    }
  };

  useEffect(() => {
    if (!composeOpen || composeType !== 'health_follow_up') return;
    let c = false;
    api
      .get('/api/health-issues', { params: { status: 'open' } })
      .then((res) => {
        if (!c) setComposeOpenIssues(res.data.issues || []);
      })
      .catch(() => {
        if (!c) setComposeOpenIssues([]);
      });
    return () => {
      c = true;
    };
  }, [composeOpen, composeType]);

  const submitQuickStandalonePost = async () => {
    const dayKey = composeTargetDayKey ?? format(new Date(), 'yyyy-MM-dd');
    setComposeSaving(true);
    try {
      await api.post('/api/training-log/posts', {
        body: composeBody.trim(),
        logDate: dayKey,
      });
      setComposeOpen(false);
      setComposeTargetDayKey(null);
      setComposeTitle('');
      setComposeBody('');
      setComposeType('journal');
      await loadItems();
    } catch (e) {
      console.error(e);
    } finally {
      setComposeSaving(false);
    }
  };

  const staggerStep = useMemo(() => {
    const n = Math.max(clusters.length, 1);
    return Math.min(0.07, 0.9 / n);
  }, [clusters.length]);

  const handleTypeVisibilityChange = (typeKey, checked) => {
    const all = logTypes.length ? logTypes.map((x) => x.type_key) : Object.keys(TYPE_LABEL_KEYS);
    const base = logPrefsRef.current.visibleTypeKeys ?? all;
    const set = new Set(base);
    if (checked) set.add(typeKey);
    else set.delete(typeKey);
    const arr = all.filter((k) => set.has(k));
    if (arr.length === all.length) updateLogPrefs({ visibleTypeKeys: null });
    else updateLogPrefs({ visibleTypeKeys: arr });
  };

  const isTypeVisible = (typeKey) => {
    const keys = logPrefs.visibleTypeKeys;
    if (keys === null || keys === undefined) return true;
    return keys.includes(typeKey);
  };

  if (!prefsLoaded) return null;

  const jumpDatePopoverFields = (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{t('trainingLog.jumpToDatePopoverHint')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="training-log-jump-date" className="sr-only">
          {t('trainingLog.jumpToDateLabel')}
        </Label>
        <Input
          ref={jumpDateInputRef}
          id="training-log-jump-date"
          type="date"
          min={format(jumpPolicyMin, 'yyyy-MM-dd')}
          max={format(jumpPolicyMax, 'yyyy-MM-dd')}
          value={jumpDateInput}
          onChange={(e) => {
            const v = e.target.value;
            setJumpDateInput(v);
            if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
              applyJumpToDate(v);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyJumpToDate();
          }}
          className="h-9 w-[min(11.5rem,100%)] shrink-0"
        />
        <Button type="button" size="sm" className="shrink-0" onClick={() => applyJumpToDate()}>
          {t('trainingLog.goToDate')}
        </Button>
      </div>
    </div>
  );

  const renderJumpCalendarTrigger = (tooltipSide) => (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(TRAINING_LOG_DATE_TOOL_BTN_CLASS, 'relative z-10')}
          aria-label={t('trainingLog.scrollToToday')}
          aria-haspopup="true"
          aria-expanded={jumpDateOpen}
          title={t('trainingLog.todayTooltipShort')}
          onPointerDown={onTodayPointerDown}
          onPointerUp={onTodayPointerUpCancel}
          onPointerLeave={onTodayPointerUpCancel}
          onPointerCancel={onTodayPointerUpCancel}
          onClick={onTodayClick}
        >
          <Calendar className="h-4 w-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} className="max-w-[16rem] space-y-1 text-xs">
        <p className="font-medium text-foreground">{t('trainingLog.todayIconTitle')}</p>
        <p className="text-muted-foreground">{t('trainingLog.todayTooltipClick')}</p>
        <p className="text-muted-foreground">{t('trainingLog.todayTooltipLong')}</p>
      </TooltipContent>
    </Tooltip>
  );

  const renderMainDiaryControl = (tooltipSide) => (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className={TRAINING_LOG_MAIN_DIARY_BTN_CLASS}
          title={t('trainingLog.toggle')}
          onClick={cycleMode}
        >
          <BookMarked className="h-4 w-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{t('trainingLog.toggle')}</TooltipContent>
    </Tooltip>
  );

  const renderSettingsControl = (tooltipSide) => (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className={cn(TRAINING_LOG_DATE_TOOL_BTN_CLASS, 'relative z-10')}
          aria-label={t('trainingLog.openSettings')}
          onClick={() => setSettingsOpen(true)}
        >
          <Settings className="h-4 w-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{t('trainingLog.openSettings')}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="citius-training-log-dock">
      {panelMode === 'hidden' && (
        <div
          className="pointer-events-auto fixed z-[3] flex flex-col items-center"
          style={{
            top: TRAINING_LOG_DOCK_TOP_PX,
            right: TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX,
          }}
        >
          {renderMainDiaryControl('left')}
        </div>
      )}
      <motion.aside
        ref={dockAsideRef}
        className={cn(
          'fixed z-[2] flex flex-row overflow-hidden',
          panelMode === 'hidden'
            ? 'right-0 pointer-events-none border-0 bg-transparent shadow-none ring-0'
            : TRAINING_LOG_DOCK_WIDGET_SURFACE_CLASS
        )}
        style={{
          top: TRAINING_LOG_DOCK_TOP_PX,
          bottom: TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX,
          right: TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX,
          left: 'auto',
        }}
        initial={false}
        animate={{ width: panelWidth }}
        transition={{ type: 'spring', stiffness: 420, damping: 38 }}
        onAnimationComplete={bumpScrollPortHeight}
      >
        <AnimatePresence>
          {panelMode !== 'hidden' && (
            <motion.div
              key="inner"
              className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div
                className={cn(
                  'relative flex min-h-0 min-w-0 flex-1 flex-col',
                  panelMode === 'peek' && 'items-center'
                )}
              >
                {panelMode === 'open' && (
                  <div className="relative z-[2] w-full shrink-0 bg-transparent">
                    {/*
                      px-1 matches timeline scroll wrapper; grid pr-4 + w-10 column matches railColumnRef (right-4 + w-10).
                      Same inner width as the timeline track so icon centers align with OPEN_LINE_LEFT.
                    */}
                    <div className="px-1">
                      <div className="grid min-w-0 w-full grid-cols-[minmax(0,1fr)_2.5rem] gap-x-10 gap-y-1.5 pb-2 pl-2 pr-4 pt-2">
                        <div className="col-start-1 row-start-1 flex min-h-9 min-w-0 items-center pr-1">
                          <span className="min-w-0 truncate text-xs font-medium text-foreground">
                            {t('trainingLog.title')}
                          </span>
                        </div>
                        <div className="pointer-events-auto relative z-20 col-start-2 row-start-1 flex w-full flex-col items-center gap-1.5 self-start">
                          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
                            {renderMainDiaryControl('left')}
                            <div className="pointer-events-auto absolute right-full top-1/2 z-10 mr-1.5 -translate-y-1/2">
                              {renderSettingsControl('left')}
                            </div>
                          </div>
                        </div>
                        <div className="col-start-1 row-start-2 flex min-w-0 flex-col justify-center gap-0.5 self-center pr-1">
                          <div className="w-full max-w-[calc(100%-8px)] min-w-0">
                            <TrainingLogOpenSearchInput
                              committedQuery={committedTimelineSearch}
                              onCommittedChange={onTimelineSearchCommitted}
                              placeholder={t('trainingLog.searchPlaceholder')}
                              ariaLabel={t('trainingLog.searchPlaceholder')}
                              busy={searchInProgress}
                              busyLabel={t('trainingLog.searchLoading')}
                            />
                          </div>
                          {searchRemoteMiss && (
                            <p className="text-[10px] leading-tight text-muted-foreground">
                              {t('trainingLog.searchNoMatches')}
                            </p>
                          )}
                        </div>
                        <div className="pointer-events-auto relative z-20 col-start-2 row-start-2 flex w-full flex-col items-center justify-center self-center">
                          <Popover open={jumpDateOpen} onOpenChange={setJumpDateOpen} modal={false}>
                            <PopoverAnchor asChild>
                              <div className="flex shrink-0 items-center">{renderJumpCalendarTrigger('left')}</div>
                            </PopoverAnchor>
                            <PopoverContent
                              side="left"
                              align="center"
                              sideOffset={6}
                              collisionPadding={12}
                              className="z-[200] w-auto min-w-[min(17rem,92vw)] max-w-[92vw] border-border/80 bg-popover p-3 shadow-md"
                              onOpenAutoFocus={(e) => e.preventDefault()}
                              onCloseAutoFocus={(e) => e.preventDefault()}
                            >
                              {jumpDatePopoverFields}
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {panelMode === 'peek' && (
                  <div className="relative z-[2] w-full max-w-full shrink-0 bg-transparent px-0 pb-0">
                    <div className="relative flex w-full flex-col items-center pt-2">
                      <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-0 bg-card/80 backdrop-blur-md"
                        style={{ bottom: PEEK_HEADER_CAP_BOTTOM_INSET }}
                        aria-hidden
                      />
                      <div className="pointer-events-auto relative z-10 flex flex-col items-center gap-1.5 isolate">
                        {renderMainDiaryControl('bottom')}
                        <Popover open={jumpDateOpen} onOpenChange={setJumpDateOpen} modal={false}>
                          <PopoverAnchor asChild>
                            <div className="flex shrink-0 items-center">{renderJumpCalendarTrigger('bottom')}</div>
                          </PopoverAnchor>
                          <PopoverContent
                            side="bottom"
                            align="center"
                            sideOffset={6}
                            collisionPadding={12}
                            className="z-[200] w-auto min-w-[min(17rem,92vw)] max-w-[92vw] border-border/80 bg-popover p-3 shadow-md"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                          >
                            {jumpDatePopoverFields}
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    'flex min-h-0 min-w-0 flex-1 flex-col self-stretch w-full',
                    panelMode === 'peek' ? 'px-0' : 'px-1'
                  )}
                >
                  <div
                    ref={timelineScrollRef}
                    className="citius-scrollbar relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto"
                  >
                    {loading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/40 backdrop-blur-[2px]">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    )}

                    <div
                      className="relative flex min-h-0 w-full flex-1 flex-col pb-2 pt-0"
                      style={timelineTrackMinStyle}
                    >
                      <div
                        ref={railColumnRef}
                        className={cn(
                          'pointer-events-none absolute z-0 w-10',
                          panelMode === 'open' ? 'top-0 right-4 bottom-0' : 'top-2 left-1/2 bottom-0 -translate-x-1/2'
                        )}
                      >
                        <div className="relative h-full min-h-0 w-full" aria-hidden />
                      </div>

                      <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-[8]"
                        style={timelineTrackMinStyle}
                      >
                        <div
                          className="pointer-events-none absolute inset-x-0 top-0 z-[5]"
                          style={timelineTrackMinStyle}
                        >
                          <div
                            className="pointer-events-none absolute bottom-0 top-0 w-1"
                            style={{
                              left: panelMode === 'open' ? OPEN_LINE_LEFT : '50%',
                              transform: 'translateX(-50%)',
                            }}
                          >
                            {logPrefs.showAcwrLine
                              ? acwrSegments.map((seg) => (
                                  <div
                                    key={seg.key}
                                    className="absolute left-0 w-full overflow-hidden rounded-full"
                                    style={
                                      seg.tail
                                        ? {
                                            top: seg.top,
                                            bottom: 0,
                                            opacity: 0.95,
                                            ...(seg.dashed
                                              ? acwrPastDashStyle(seg.acwr)
                                              : { backgroundColor: acwrToHeatmapColor(seg.acwr) }),
                                          }
                                        : {
                                            top: seg.top,
                                            height: seg.height,
                                            opacity: 0.95,
                                            ...(seg.dashed
                                              ? acwrPastDashStyle(seg.acwr)
                                              : { backgroundColor: acwrToHeatmapColor(seg.acwr) }),
                                          }
                                    }
                                  />
                                ))
                              : (
                                  <>
                                    <div
                                      className="pointer-events-none absolute left-0 top-0 w-0 border-l-2 border-dashed border-primary/40"
                                      style={{ height: Math.max(ySplit, 28) }}
                                    />
                                    <div
                                      className="pointer-events-none absolute left-0 w-0.5 rounded-full bg-gradient-to-b from-primary/45 via-ring/35 to-border/50"
                                      style={{ top: ySplit, bottom: 0, minHeight: 32 }}
                                    />
                                  </>
                                )}
                          </div>
                        </div>

                        <div
                          className="pointer-events-none absolute z-20 box-border flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-primary bg-card shadow-sm"
                          style={{
                            left: panelMode === 'open' ? OPEN_LINE_LEFT : '50%',
                            top: ySplit,
                            width: TODAY_MARK_PX,
                            height: TODAY_MARK_PX,
                          }}
                          aria-label={t('trainingLog.todayMarker')}
                        >
                          <span
                            className="rounded-full bg-primary"
                            style={{
                              width: Math.max(3, Math.round(TODAY_MARK_PX * 0.45)),
                              height: Math.max(3, Math.round(TODAY_MARK_PX * 0.45)),
                            }}
                          />
                        </div>

                        {emptyDayMarkers.map(({ dayKey, y }) => (
                          <div
                            key={dayKey}
                            className="pointer-events-none absolute inset-x-0 h-11"
                            style={{ top: y, transform: 'translateY(-50%)' }}
                          >
                            <div
                              className="pointer-events-auto absolute flex h-11 w-11 items-center justify-center"
                              style={{
                                left: panelMode === 'open' ? OPEN_LINE_LEFT : '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                              }}
                            >
                              <Tooltip delayDuration={150}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 shadow-none hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                                    aria-label={format(parseISO(`${dayKey}T12:00:00`), 'PPP', { locale })}
                                    onClick={() => {
                                      setComposeTargetDayKey(dayKey);
                                      setComposePickedSession(null);
                                      setComposeOpen(true);
                                    }}
                                  >
                                    <span
                                      className="shrink-0 rounded-full border-2 border-background bg-muted-foreground shadow-sm ring-1 ring-border pointer-events-none"
                                      style={{ width: EMPTY_DOT_PX, height: EMPTY_DOT_PX }}
                                    />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="z-[120] text-xs">
                                  {format(parseISO(`${dayKey}T12:00:00`), 'PPP', { locale })}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                      </div>

                  <motion.div
                    className="pointer-events-none relative z-[12] w-full"
                    style={timelineTrackMinStyle}
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: {},
                      show: {
                        transition: {
                          staggerChildren: staggerStep,
                          delayChildren: 0.05,
                          duration: 1,
                        },
                      },
                    }}
                  >
                    {clusters.map(({ y, items: clusterItems, primary, count }) => {
                      const searchHitOnDay =
                        !!committedSearchLower &&
                        clusterItems.some((it) => itemMatchesSearchLower(it, committedSearchLower));
                      const colorClass =
                        COLOR_MAP[primary.color_token] || COLOR_MAP.primary;
                      const dateStr = formatTimelineClusterRowDate(primary.occurred_at, locale);
                      const tooltipDateStr = format(new Date(primary.occurred_at), 'PPP', { locale });
                      const titleStr = primary.title || t('trainingLog.untitled');
                      return (
                        <motion.div
                          key={clusterItems.map((it) => it.id).join('-')}
                          className="pointer-events-none absolute inset-x-0 z-[12] h-11"
                          style={{ top: y }}
                          variants={{
                            hidden: { opacity: 0, y: '-50%' },
                            show: {
                              opacity: 1,
                              y: '-50%',
                              transition: { duration: 0.35, ease: 'easeOut' },
                            },
                          }}
                        >
                          {panelMode === 'open' && (
                            <div
                              className="pointer-events-none absolute z-[1] max-w-[min(220px,calc(100%-5rem))] top-1/2 min-h-[2.75rem] -translate-y-1/2 space-y-0.5 text-right leading-snug"
                              style={{ right: OPEN_LABEL_RIGHT }}
                            >
                              <span className="block truncate text-[11px] font-medium text-foreground">
                                {titleStr}
                              </span>
                              <span className="block text-[10px] text-muted-foreground">{dateStr}</span>
                            </div>
                          )}
                          <div
                            className="pointer-events-auto absolute z-[12] flex h-11 w-11 items-center justify-center"
                            style={{
                              left: panelMode === 'open' ? OPEN_LINE_LEFT : '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 shadow-none"
                                  onClick={() => {
                                    setDayModalClusterItems(clusterItems);
                                    setDayModalOpen(true);
                                  }}
                                >
                                  <span
                                    className={cn(
                                      'relative box-border flex shrink-0 items-center justify-center rounded-full border shadow-sm',
                                      colorClass,
                                      searchHitOnDay &&
                                        'ring-2 ring-primary/80 ring-offset-2 ring-offset-background'
                                    )}
                                    style={{ width: DOT_VISUAL_PX, height: DOT_VISUAL_PX }}
                                  >
                                    <TypeIcon
                                      iconKey={primary.icon_key}
                                      className="pointer-events-none h-3.5 w-3.5 shrink-0 opacity-95"
                                    />
                                  </span>
                                  {count > 1 && (
                                    <Badge
                                      variant="secondary"
                                      className="absolute -right-0.5 -top-0.5 min-h-4 min-w-4 px-1 py-0.5 text-[8px] leading-none tabular-nums"
                                    >
                                      {count}
                                    </Badge>
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                align="end"
                                sideOffset={6}
                                className="z-[120] max-w-[min(280px,85vw)] text-xs text-right"
                              >
                                <p className="font-medium leading-tight text-foreground">{tooltipDateStr}</p>
                                {count > 1 ? (
                                  <ul className="mt-1.5 space-y-1 text-right">
                                    {clusterItems.map((it) => (
                                      <li key={it.id} className="text-[11px] leading-snug">
                                        <span className="font-medium text-foreground">
                                          {it.title || t('trainingLog.untitled')}
                                        </span>
                                        {!!it.summary && (
                                          <span className="mt-0.5 block text-[10px] text-muted-foreground">
                                            {it.summary}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <>
                                    <p className="mt-1 font-medium leading-snug text-foreground">{titleStr}</p>
                                    {!!primary.summary && (
                                      <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                                        {primary.summary}
                                      </p>
                                    )}
                                  </>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                    </div>
                  </div>

                  <div className="pointer-events-auto relative z-[3] h-[5.5rem] w-full shrink-0">
                    <div
                      className="absolute bottom-2 flex flex-col items-center gap-2"
                      style={{
                        left: panelMode === 'open' ? OPEN_LINE_LEFT : '50%',
                        transform: 'translateX(-50%)',
                      }}
                    >
                      {panelMode !== 'hidden' && (
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className={TRAINING_LOG_DATE_TOOL_BTN_CLASS}
                              aria-label={t('trainingLog.plannerOpen')}
                              onClick={() => setPlannerOpen(true)}
                            >
                              <span className="relative flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
                                <Calendar className="absolute left-0 top-0 h-3 w-3 opacity-90" />
                                <Dumbbell className="absolute bottom-0 right-0 h-2.5 w-2.5 text-primary" />
                              </span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-[14rem] text-xs">
                            {t('trainingLog.plannerOpen')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            className="h-9 w-9 rounded-full shadow-md"
                            onClick={() => {
                              setComposeTargetDayKey(null);
                              setComposePickedSession(null);
                              setComposeOpen(true);
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left">{t('trainingLog.createEntry')}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.aside>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-[min(90vw,440px)] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('trainingLog.settingsTitle')}</DialogTitle>
            <DialogDescription>{t('trainingLog.settingsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tl-viewport-weeks" className="text-sm">
                {t('trainingLog.viewportWeeksLabel')}
              </Label>
              <Select
                value={String(logPrefs.timelineViewportWeeks === 2 ? 2 : 1)}
                onValueChange={(v) =>
                  updateLogPrefs({ timelineViewportWeeks: v === '2' ? 2 : 1 })
                }
              >
                <SelectTrigger id="tl-viewport-weeks" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <SelectItem value="1">{t('trainingLog.viewportWeeks1')}</SelectItem>
                  <SelectItem value="2">{t('trainingLog.viewportWeeks2')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('trainingLog.viewportWeeksHint')}</p>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t('trainingLog.visibleTypesHeading')}
              </p>
              <div className="space-y-3">
                {(logTypes.length ? logTypes : []).map((row) => (
                  <div
                    key={row.type_key}
                    className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2"
                  >
                    <Label htmlFor={`tl-type-${row.type_key}`} className="cursor-pointer text-sm">
                      {t(TYPE_LABEL_KEYS[row.type_key] || `trainingLog.type_${row.type_key}`)}
                    </Label>
                    <Switch
                      id={`tl-type-${row.type_key}`}
                      checked={isTypeVisible(row.type_key)}
                      onCheckedChange={(c) => handleTypeVisibilityChange(row.type_key, c)}
                    />
                  </div>
                ))}
                {!logTypes.length && (
                  <p className="text-xs text-muted-foreground">{t('trainingLog.settingsTypesLoading')}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <div className="min-w-0">
                <Label htmlFor="tl-show-acwr-line" className="cursor-pointer text-sm">
                  {t('trainingLog.showAcwrLine')}
                </Label>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('trainingLog.showAcwrLineHint')}</p>
              </div>
              <Switch
                id="tl-show-acwr-line"
                checked={!!logPrefs.showAcwrLine}
                onCheckedChange={(c) => updateLogPrefs({ showAcwrLine: !!c })}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {logPrefs.showAcwrLine ? t('trainingLog.heatmapLegend') : t('trainingLog.lineStyleHint')}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="default" onClick={() => setSettingsOpen(false)}>
              {t('trainingLog.settingsDone')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={composeOpen}
        onOpenChange={(open) => {
          setComposeOpen(open);
          if (!open) {
            setComposeTargetDayKey(null);
            setComposePickedSession(null);
            setComposeHealthIssueId(null);
          }
        }}
      >
        <DialogContent className="max-w-[min(90vw,420px)]">
          <DialogHeader>
            <DialogTitle>{t('trainingLog.createEntry')}</DialogTitle>
            {!!composeTargetDayKey && (
              <DialogDescription>
                {t('trainingLog.composeForDate', {
                  date: format(parseISO(`${composeTargetDayKey}T12:00:00`), 'PPP', { locale }),
                })}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('trainingLog.entryType')}</Label>
              <Select
                value={composeType}
                onValueChange={(v) => {
                  setComposeType(v);
                  if (v !== 'planned_session') setComposePickedSession(null);
                  if (v !== 'health_follow_up') setComposeHealthIssueId(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="journal">{t('trainingLog.typeJournal')}</SelectItem>
                  <SelectItem value="planned_session">{t('trainingLog.typePlannedSession')}</SelectItem>
                  <SelectItem value="race">{t('trainingLog.typeRace')}</SelectItem>
                  <SelectItem value="goal">{t('trainingLog.typeGoal')}</SelectItem>
                  <SelectItem value="health_new_issue">{t('trainingLog.typeHealthNewIssue')}</SelectItem>
                  <SelectItem value="health_follow_up">{t('trainingLog.typeHealthFollowUp')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {composeType === 'health_follow_up' && (
              <div className="space-y-1">
                <Label>{t('trainingLog.healthFollowPickIssue')}</Label>
                <Select
                  value={composeHealthIssueId ? String(composeHealthIssueId) : ''}
                  onValueChange={(v) => setComposeHealthIssueId(v ? parseInt(v, 10) : null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('trainingLog.healthFollowPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {composeOpenIssues.map((iss) => (
                      <SelectItem key={iss.id} value={String(iss.id)}>
                        {(iss.title || iss.issue_kind) + ' · #' + iss.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {composeType === 'planned_session' && (
              <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => setComposeSessionPickerOpen(true)}
                >
                  {t('training.composePickSession')}
                </Button>
                {composePickedSession?.name && (
                  <p className="text-xs text-muted-foreground">
                    {t('trainingLog.pickedSession')}:{' '}
                    <span className="font-medium text-foreground">{composePickedSession.name}</span>
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1">
              <Label>{t('trainingLog.entryTitle')}</Label>
              <Input value={composeTitle} onChange={(e) => setComposeTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>{t('trainingLog.entryBody')}</Label>
              <Textarea rows={4} value={composeBody} onChange={(e) => setComposeBody(e.target.value)} />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              variant="ghost"
              type="button"
              className="w-full text-xs text-muted-foreground"
              disabled={composeSaving}
              onClick={submitQuickStandalonePost}
            >
              {t('trainingLog.saveQuickNote')}
            </Button>
            <div className="flex w-full justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setComposeOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="button" disabled={composeSaving} onClick={submitCompose}>
                {composeSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('trainingLog.saveEntry')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SessionPickerModal
        open={composeSessionPickerOpen}
        onOpenChange={setComposeSessionPickerOpen}
        onSelect={(sess) => {
          setComposePickedSession({ id: sess.id, name: sess.name });
          setComposeTitle(sess.name || '');
        }}
      />

      <TrainingLogDayModal
        open={dayModalOpen}
        onOpenChange={setDayModalOpen}
        clusterItems={dayModalClusterItems}
        onDataChanged={loadItems}
      />

      <TrainingPlannerModal
        open={plannerOpen}
        onOpenChange={setPlannerOpen}
        onPlannedChanged={loadItems}
      />
    </div>
  );
});

export default TrainingLogDock;
